import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import SectionLabel from "../SectionLabel";
import Picture from "../Picture";
import { PHILOSOPHY_POINTS, PHILOSOPHY_IMAGE } from "../../data/content";

export default function Philosophy() {
    return (
        <section
            className="relative section-pad bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.12)] overflow-hidden"
            data-testid="philosophy-section"
        >
            <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                {/* Left — editorial image */}
                <motion.div
                    suppressHydrationWarning
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative overflow-hidden aspect-[4/5] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full">
                        <Picture
                            slug={PHILOSOPHY_IMAGE}
                            alt="A quiet interior, richly detailed — the kind of residence ACT is trusted with."
                            sizes="(max-width: 1024px) 100vw, 40vw"
                            className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/40 via-transparent to-transparent" />
                    </div>
                    <div className="mt-6 flex items-center gap-4">
                        <span className="h-px w-12 bg-[#C9A86A]/60" />
                        <span className="text-[11px] tracking-[0.32em] uppercase text-[#9A958A]">
                            Est. 2026 · United States
                        </span>
                    </div>
                </motion.div>

                {/* Right — story + checklist */}
                <div className="lg:col-span-7">
                    <SectionLabel number="—" label="Philosophy" />
                    <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
                        {"A studio practice, not "}
                        <span className="italic text-[#C9A86A]">
                            a contractor&rsquo;s trade.
                        </span>
                    </h2>
                    <p className="mt-8 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-xl">
                        We work in residences where the millwork is hand-finished
                        and the stone is book-matched. Our standard is a match
                        for that craftsmanship — a small, named team, a written
                        design narrative, and a lifetime of quiet stewardship.
                    </p>

                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                        {PHILOSOPHY_POINTS.map((point, idx) => (
                            <motion.div
                                key={point.title}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.7,
                                    delay: idx * 0.08,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="flex gap-4"
                            >
                                <span className="mt-1 flex-none inline-flex items-center justify-center w-7 h-7 border border-[#C9A86A]/60 text-[#C9A86A]">
                                    <Check size={13} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <h3 className="font-serif text-xl text-[#F3EFE7] mb-1.5">
                                        {point.title}
                                    </h3>
                                    <p className="text-sm text-[#9A958A] leading-[1.85]">
                                        {point.copy}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-14">
                        <Link
                            to="/about"
                            className="gold-link"
                            data-testid="philosophy-read-story"
                        >
                            Read Our Story
                            <ArrowUpRight size={14} strokeWidth={1.2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
